import { useCallback } from "react";
import { toast } from "react-hot-toast";
import { BsArrowUpCircle, BsFillArrowUpCircleFill } from "react-icons/bs";
import { useAppAuthState } from "../../hooks/use-app-auth-state";
import { formatTimestampNoTime } from "../../lib/format-timestamp";
import { toggleUpvote } from "../../services/podcastService";
import { Podcast } from "../../types/Podcast";
import "./PodcastCard.scss";

interface PodcastCardProps {
  podcast: Podcast;
  onUpvote: () => void;
}

const PodcastCard: React.FC<PodcastCardProps> = ({ podcast, onUpvote }) => {
  const { fireUser, isLoggedIn } = useAppAuthState();

  const handleUpvote = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();

      if (!isLoggedIn || !fireUser) {
        toast.error("You must be logged in to upvote a podcast.");
        return;
      }

      try {
        const direction = await toggleUpvote(podcast.id, fireUser.uid);
        direction === 1 && toast.success("Upvoted!");
        onUpvote();
      } catch (error) {
        toast.error("Something went wrong.");
      }
    },
    [isLoggedIn, fireUser, podcast.id, onUpvote],
  );

  const hasUpvoted = (podcast.userVotes ?? []).some(
    (vote) => vote.userId === fireUser?.uid,
  );

  return (
    <div
      key={podcast.id}
      className="podcast-card"
      onClick={() => {
        window.open(podcast.url, "_blank");
      }}
    >
      <div className="podcast-card-left">
        <div className="podcast-card-header">
          <div className="podcast-card-title">{podcast.title}</div>
          {!!podcast.description && (
            <div className="podcast-card-description">
              {podcast.description}
            </div>
          )}
        </div>
        <div className="podcast-card-footer">
          <div className="podcast-card-footer-left">
            <span>{podcast.url}</span>
            <span className="separator">•</span>
            <span className="timestamp">
              {formatTimestampNoTime(podcast.createdAt)}
            </span>
            {podcast.participants.length > 0 && (
              <>
                <span className="separator">•</span>
                <span className="participants">
                  {podcast.participants.join(", ")}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="podcast-card-right">
        <div className="upvote-section">
          <button className="upvote-button" onClick={handleUpvote}>
            <>
              {hasUpvoted ? (
                <BsFillArrowUpCircleFill size={24} />
              ) : (
                <BsArrowUpCircle size={24} />
              )}
            </>
          </button>
          <div className="upvote-count">{podcast.userVotes.length}</div>
        </div>
      </div>
    </div>
  );
};

export default PodcastCard;
